import { useEffect, useState } from "react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Avatar } from "@/components/Avatar";
import { Counter } from "@/components/Counter";
import { FORMATION, POS_LABEL, TEAM, perms } from "@/lib/football";
import { useAuth } from "@/context/AuthContext";

const SlotGrid = ({ data, player, onSlot }) => {
  const byId = Object.fromEntries(data.players.map((p) => [p.id, p]));
  const occ = Object.fromEntries(data.positions.filter((p) => p.team === player.team && p.slot !== null).map((p) => [p.slot, byId[p.player_id]]));
  const mine = data.positions.find((p) => p.player_id === player.id)?.slot;
  return (
    <div className="mt-5">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Changer de poste</p>
      <div className="grid grid-cols-4 gap-1.5">
        {FORMATION.map(([code], i) => (
          <button key={i} data-testid={`slot-option-${i}`} disabled={mine === i} onClick={() => onSlot(i)}
            className={`rounded-xl px-1 py-2 text-center border transition-colors active:scale-95 ${mine === i ? "bg-cyan-400 text-zinc-950 border-cyan-300" : "bg-slate-50 border-slate-200"}`}>
            <span className="num text-xs font-bold block">{code}</span>
            <span className="text-[10px] block truncate opacity-70">{occ[i] ? occ[i].short_name : "Libre"}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const PlayerSheet = ({ data, player, onClose, act }) => {
  const { user } = useAuth();
  const stat = data.stats.find((s) => s.player_id === player?.id);
  const [goals, setGoals] = useState(0);
  const [assists, setAssists] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setGoals(stat?.goals || 0);
    setAssists(stat?.assists || 0);
  }, [player?.id, stat?.goals, stat?.assists]);
  if (!player) return null;
  const pm = perms(data, user);
  const pos = data.positions.find((p) => p.player_id === player.id);

  const save = async () => {
    setSaving(true);
    const r = await act("put", `/stats/${player.id}`, { goals, assists }, `Stats de ${player.short_name} enregistrées`);
    setSaving(false);
    if (r) onClose();
  };

  return (
    <Drawer open={!!player} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent data-testid="player-sheet" className="bg-white text-slate-900 max-w-md mx-auto">
        <div className="px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2 overflow-y-auto max-h-[80dvh]">
          <div className="flex items-center gap-3">
            <Avatar user={player} className="w-14 h-14 text-base" ring={player.team === "white" ? "border-2 border-slate-300" : "border-2 border-zinc-950"} />
            <div>
              <DrawerTitle className="font-display text-xl font-bold" data-testid="player-sheet-name">{player.short_name}</DrawerTitle>
              <DrawerDescription className="text-xs text-slate-500">
                {player.is_guest ? "Invité · " : ""}{player.team ? TEAM[player.team].full : ""}{pos ? ` · ${POS_LABEL[pos.assigned_position] || pos.assigned_position}` : ""}
                {pos && pos.assigned_position !== player.primary_position ? ` (profil : ${player.primary_position})` : ""}
              </DrawerDescription>
            </div>
          </div>
          {pm.editStats ? (
            <div className="mt-5 space-y-2.5">
              <Counter label="Buts" value={goals} onChange={setGoals} testid="goals-counter" />
              <Counter label="Passes décisives" value={assists} onChange={setAssists} testid="assists-counter" />
              <button data-testid="save-stats-button" disabled={saving} onClick={save}
                className="w-full mt-2 rounded-2xl bg-cyan-400 text-zinc-950 font-display font-black tracking-wide py-4 active:scale-[0.98] transition-transform disabled:opacity-50">
                {saving ? "ENREGISTREMENT…" : "ENREGISTRER"}
              </button>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-2">
              <div className="rounded-2xl bg-cyan-50 p-3 text-center"><p className="num text-2xl font-bold">{stat?.goals || 0}</p><p className="text-xs text-slate-500">Buts</p></div>
              <div className="rounded-2xl bg-violet-50 p-3 text-center"><p className="num text-2xl font-bold">{stat?.assists || 0}</p><p className="text-xs text-slate-500">Passes décisives</p></div>
            </div>
          )}
          {player.team && pm.editLineup?.(player.team) && pos && (
            <SlotGrid data={data} player={player} onSlot={(slot) => act("post", "/lineup/slot", { user_id: player.id, slot }, "Poste modifié").then((r) => r && onClose())} />
          )}
          {pm.admin && data.match.status === "scheduled" && player.team && !player.is_captain && (
            <button data-testid="switch-team-button" onClick={() => act("post", "/teams/move", { user_id: player.id, team: player.team === "white" ? "black" : "white" }, "Joueur déplacé").then((r) => r && onClose())}
              className="w-full mt-4 rounded-2xl bg-slate-100 border border-slate-200 py-3 text-sm font-semibold active:scale-[0.98]">
              Passer en {player.team === "white" ? "Équipe Noire" : "Équipe Blanche"}
            </button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
